import { warn } from '@ember/debug';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import onResize from 'ember-on-resize-modifier/modifiers/on-resize';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvBtn from 'volta/components/bv-btn';
import BvDropdownMenu from 'volta/components/bv-dropdown-menu';
import BulkActionBtn from 'volta/components/bv-resource-list/bulk-action-btn';
import CheckableBtn from 'volta/components/bv-resource-list/checkable-btn';
import classNames from 'volta/helpers/class-names';

import type { ContentValue } from '@glint/template';
import type { IntlService } from 'ember-intl';
import type { IBulkAction, IBulkActionSection } from '.';

const MAX_PROMOTED_ACTIONS = 2;

interface BvResourceListBulkActionsSignature {
  Args: {
    /**
     * Label for the bulk actions
     */
    label?: string;
    /**
     * Visually hidden text for screen readers
     */
    accessibilityLabel?: string;
    /**
     * State of the bulk actions checkbox
     */
    selected?: boolean | string;
    /**
     * List is in a selectable state
     */
    selectMode: boolean;
    /**
     * Disables bulk actions
     */
    disabled?: boolean;
    /**
     * Text to select all across pages
     */
    paginatedSelectAllText?: string;
    /**
     * Action for selecting all across pages
     */
    paginatedSelectAllAction?: { onClick: () => void; content: ContentValue };
    /**
     * List of actions
     */
    bulkActions?: (IBulkAction | IBulkActionSection | undefined)[];
    /**
     * Actions that will be given more prominence
     */
    promotedBulkActions?: (IBulkAction | undefined)[];

    /**
     * Callback when selectable state of list is changed
     */
    onSelectModeToggle?: (selectMode: boolean) => void;
    /**
     * Callback when the select all checkbox is clicked
     */
    onToggleAll?: () => void;
  };
}

export default class BvResourceListBulkActionsComponent extends Component<BvResourceListBulkActionsSignature> {
  @service intl!: IntlService;

  /* Getters */

  get label() {
    return this.args.label ?? '';
  }

  get promotedBulkActions(): IBulkAction[] {
    return (this.args.promotedBulkActions ?? []).filter((a) => a != null);
  }

  get bulkActions() {
    return (this.args.bulkActions ?? []).filter((a) => a != null);
  }

  //onSelectModeToggle() {}

  /* Private State */

  @tracked containerWidth = 0;
  @tracked measuring = true;

  bulkActionsWidth = 0;
  addedMoreActionsWidthForMeasuring = 0;
  @ref('containerNode') containerNode?: HTMLElement;
  @ref('largeScreenButtonsNode') largeScreenButtonsNode?: HTMLElement;
  @ref('moreActionsNode') moreActionsNode?: HTMLElement;
  promotedActionsWidths: number[] = [];

  /* Computed Properties */

  get promotedBulkActionsToRender() {
    if (this.promotedBulkActions.length > 0 && this.numberOfPromotedActionsToRender > 0) {
      return this.promotedBulkActions.slice(0, this.numberOfPromotedActionsToRender);
    }
    return [];
  }

  get numberOfPromotedActionsToRender() {
    const { measuring, containerWidth } = this;

    if (isEmpty(this.promotedBulkActions)) {
      return 0;
    }

    if (containerWidth >= this.bulkActionsWidth || measuring) {
      return this.promotedBulkActions.length;
    }

    let sufficientSpace = false;
    let counter = this.promotedBulkActions.length - 1;
    let totalWidth = 0;

    while (!sufficientSpace && counter >= 0) {
      totalWidth += this.promotedActionsWidths[counter] ?? 0;
      const widthWithRemovedAction =
        this.bulkActionsWidth - totalWidth + this.addedMoreActionsWidthForMeasuring;
      if (containerWidth >= widthWithRemovedAction) {
        sufficientSpace = true;
      } else {
        counter--;
      }
    }

    return counter;
  }

  get hasActions() {
    return this.promotedBulkActions.length || this.bulkActions.length;
  }

  get actionSections(): IBulkActionSection[] | undefined {
    const { bulkActions } = this;
    if (instanceOfBulkActionListSectionArray(bulkActions)) {
      return bulkActions as IBulkActionSection[];
    }

    if (instanceOfBulkActionArray(bulkActions)) {
      return [
        {
          items: bulkActions as IBulkAction[]
        }
      ];
    }
    return undefined;
  }

  get rolledInPromotedActions() {
    return this.promotedBulkActions &&
      this.numberOfPromotedActionsToRender < this.promotedBulkActions.length
      ? [...(this.promotedBulkActions.filter(Boolean) as IBulkAction[])].slice(
          this.numberOfPromotedActionsToRender
        )
      : [];
  }

  get combinedActions() {
    let actions: IBulkActionSection[] = [];

    if (this.actionSections && this.rolledInPromotedActions.length > 0) {
      actions = [{ items: this.rolledInPromotedActions }, ...this.actionSections];
    } else if (this.actionSections) {
      actions = this.actionSections;
    } else if (this.rolledInPromotedActions.length > 0) {
      actions = [{ items: this.rolledInPromotedActions }];
    }

    return actions;
  }

  get activatorLabel() {
    return !this.promotedBulkActions ||
      (this.promotedBulkActions && this.numberOfPromotedActionsToRender === 0 && !this.measuring)
      ? this.intl.t('bvList.bulkActions.actionsActivatorLabel')
      : this.intl.t('bvList.bulkActions.moreActionsActivatorLabel');
  }

  /* Lifecycle Hooks */

  @action
  handleDidUpdate(_containerNode: HTMLElement, [promotedBulkActions]: [IBulkAction[]]) {
    if (promotedBulkActions && promotedBulkActions.length > MAX_PROMOTED_ACTIONS) {
      warn(
        `To provide a better user experience. There should only be a maximum of ${MAX_PROMOTED_ACTIONS} promoted actions.`,
        {
          id: 'bevolta.bv-resource-list.bulk-actions.max-promoted-actions'
        }
      );
    }
  }

  @action
  handleDidInsert(containerNode: HTMLElement) {
    const { moreActionsNode, largeScreenButtonsNode } = this;

    if (this.promotedBulkActions.length > 0 && this.bulkActions.length === 0 && moreActionsNode) {
      this.addedMoreActionsWidthForMeasuring = moreActionsNode.getBoundingClientRect().width;
    }

    this.bulkActionsWidth = largeScreenButtonsNode
      ? largeScreenButtonsNode.getBoundingClientRect().width -
        this.addedMoreActionsWidthForMeasuring
      : 0;

    if (containerNode) {
      this.containerWidth = containerNode.getBoundingClientRect().width;
      this.measuring = false;
    }
  }

  /* Actions */

  @action
  setSelectMode(selectMode: boolean) {
    return this.args.onSelectModeToggle?.(selectMode);
  }

  @action
  handleMeasurement(width: number) {
    if (this.measuring) {
      this.promotedActionsWidths.push(width);
    }
  }

  @action
  handleDidResize(/*element*/) {
    if (this.containerNode) {
      const containerWidth = this.containerNode.getBoundingClientRect().width;

      if (containerWidth > 0) {
        this.containerWidth = containerWidth;
      }
    }
  }

  <template>
    {{! template-lint-disable no-at-ember-render-modifiers }}
    <div
      class='bv-resource-list-bulk-actions'
      {{didInsert this.handleDidInsert}}
      {{didUpdate this.handleDidUpdate this.promotedBulkActions}}
      {{onResize this.handleDidResize debounce=50}}
      {{createRef 'containerNode' bucket=this}}
    >
      {{! Small Screen Group }}
      <div
        class={{classNames
          'bv-resource-list-bulk-actions__group bv-resource-list-bulk-actions__group--sm'
          (if
            @selectMode
            'bv-resource-list-bulk-actions__group--entered'
            'bv-resource-list-bulk-actions__group--exited'
          )
        }}
      >
        <div class='bv-resource-list-bulk-actions__btn-group'>
          <CheckableBtn
            @accessibilityLabel={{@accessibilityLabel}}
            @label={{@label}}
            @selected={{not (not @selected)}}
            @selectMode={{@selectMode}}
            @onToggleAll={{@onToggleAll}}
            @measuring={{this.measuring}}
            @disabled={{@disabled}}
          />
          {{!allActionsPopover}}
          {{#if this.hasActions}}
            <div class='bv-resource-list-bulk-actions__popover'>
              <BvDropdownMenu
                {{! @glint-ignore }}
                @items={{this.promotedBulkActions}}
                {{! @glint-ignore }}
                @sections={{this.actionSections}}
              >
                <:trigger as |popover|>
                  <BulkActionBtn
                    @disclosure={{true}}
                    @content={{t 'bvList.bulkActions.actionsActivatorLabel'}}
                    @onClick={{popover.toggle}}
                    {{popover.trigger}}
                  />
                </:trigger>
              </BvDropdownMenu>
            </div>
          {{/if}}
          {{!cancelButton}}
          <button
            type='button'
            class='bv-resource-list-bulk-actions__btn bv-resource-list-bulk-actions__btn--cancel'
            data-test-btn-cancel
            {{on 'click' (fn this.setSelectMode false)}}
          >
            {{t 'cancel'}}
          </button>
        </div>
        {{!paginatedSelectAllMarkup}}
        {{#if
          (or @paginatedSelectAllAction (and @paginatedSelectAllText @paginatedSelectAllAction))
        }}
          <div
            class='bv-resource-list-bulk-actions__paginated-select-all'
            data-test-paginated-select-all
          >
            {{!paginatedSelectAllTextMarkup}}
            {{#if (and @paginatedSelectAllText @paginatedSelectAllAction)}}
              <span aria-live='polite'>
                {{@paginatedSelectAllText}}
              </span>
            {{else}}
              {{@paginatedSelectAllText}}
            {{/if}}
            {{!paginatedSelectAllActionMarkup}}
            {{#if @paginatedSelectAllAction}}
              <BvBtn
                data-test-paginated-action
                @onClick={{@paginatedSelectAllAction.onClick}}
                @disabled={{@disabled}}
                @subtle={{true}}
                @size='sm'
              >
                {{@paginatedSelectAllAction.content}}
              </BvBtn>
            {{/if}}
          </div>
        {{/if}}
      </div>
      {{! Large Screen Group }}
      <div
        class={{classNames
          'bv-resource-list-bulk-actions__group bv-resource-list-bulk-actions__group--lg'
          (if this.measuring 'bv-resource-list-bulk-actions__group--measuring')
          (unless
            this.measuring
            (concat 'bv-resource-list-bulk-actions__group--' (if @selectMode 'entered' 'exited'))
          )
        }}
      >
        <div
          class='bv-resource-list-bulk-actions__btn-group'
          {{createRef 'largeScreenButtonsNode' bucket=this}}
        >
          <CheckableBtn
            @accessibilityLabel={{@accessibilityLabel}}
            @label={{@label}}
            @selected={{not (not @selected)}}
            @selectMode={{@selectMode}}
            @onToggleAll={{@onToggleAll}}
            @measuring={{this.measuring}}
            @disabled={{@disabled}}
          />
          {{!promotedActionsMarkup}}
          {{#each this.promotedBulkActionsToRender as |promotedAction|}}
            <BulkActionBtn
              @disclosure={{promotedAction.disclosure}}
              @url={{promotedAction.url}}
              @external={{promotedAction.external}}
              @onClick={{promotedAction.onClick}}
              @content={{promotedAction.content}}
              @icon={{promotedAction.icon}}
              @tooltip={{promotedAction.tooltip}}
              @accessibilityLabel={{promotedAction.accessibilityLabel}}
              @disabled={{promotedAction.disabled}}
              @handleMeasurement={{this.handleMeasurement}}
            />
          {{/each}}
          {{!actionsPopover}}
          {{#if (or this.actionSections this.rolledInPromotedActions.length this.measuring)}}
            <div
              class='bv-resource-list-bulk-actions__popover'
              {{createRef 'moreActionsNode' bucket=this}}
            >
              <BvDropdownMenu
                @sections={{this.combinedActions}}
                @horizontalPosition='right'
                @disabled={{@disabled}}
              >
                <:trigger as |popover|>
                  <BulkActionBtn
                    @disclosure={{true}}
                    @content={{this.activatorLabel}}
                    @disabled={{@disabled}}
                    @onClick={{popover.toggle}}
                    {{popover.trigger}}
                  />
                </:trigger>
              </BvDropdownMenu>
            </div>
          {{/if}}
        </div>
        {{#if
          (or @paginatedSelectAllAction (and @paginatedSelectAllText @paginatedSelectAllAction))
        }}
          <div
            class='bv-resource-list-bulk-actions__paginated-select-all'
            data-test-paginated-select-all
          >
            {{!paginatedSelectAllTextMarkup}}
            {{#if (and @paginatedSelectAllText @paginatedSelectAllAction)}}
              <span aria-live='polite'>
                {{@paginatedSelectAllText}}
              </span>
            {{else}}
              {{@paginatedSelectAllText}}
            {{/if}}
            {{!paginatedSelectAllActionMarkup}}
            {{#if @paginatedSelectAllAction}}
              <BvBtn
                data-test-paginated-action
                @onClick={{@paginatedSelectAllAction.onClick}}
                @disabled={{@disabled}}
                @size='sm'
                @subtle={{true}}
              >
                {{@paginatedSelectAllAction.content}}
              </BvBtn>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}

function instanceOfBulkActionListSectionArray(bulkActions: (IBulkAction | IBulkActionSection)[]) {
  const validList = bulkActions.filter((action: IBulkAction | IBulkActionSection) =>
    Object.prototype.hasOwnProperty.call(action, 'items')
  );
  return bulkActions.length === validList.length;
}

function instanceOfBulkActionArray(bulkActions: (IBulkAction | IBulkActionSection)[]) {
  const validList = bulkActions.filter(
    (action: IBulkAction | IBulkActionSection) =>
      !Object.prototype.hasOwnProperty.call(action, 'items')
  );
  return bulkActions.length === validList.length;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvResourceList::BulkActions': typeof BvResourceListBulkActionsComponent;
    'bv-resource-list/bulk-actions': typeof BvResourceListBulkActionsComponent;
  }
}
